/**
 * Onglet Visualisation des logs
 * @module admin/tabs/LogsTab
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useLogs } from '../hooks/useLogs';
import AdminResult from '../components/AdminResult';
import { DEFAULT_LOG_CLEANUP_DAYS } from '../constants/defaultValues';
import { FaSearch, FaCopy, FaChevronUp, FaChevronDown } from 'react-icons/fa';

/**
 * Échappe une chaîne pour l'utiliser dans une regex
 * @param {string} str - Chaîne à échapper
 * @returns {string}
 */
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Échappe le HTML pour éviter les injections
 * @param {string} str - Chaîne à échapper
 * @returns {string}
 */
function escapeHtml(str) {
  if (typeof str !== 'string') return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

const LogsTab = () => {
  const {
    logFiles,
    selectedLogFile,
    logContent,
    logLoading,
    logError,
    maintenanceLoading,
    maintenanceResult,
    maintenanceError,
    fetchLogFiles,
    fetchLogContent,
    handleCleanupLogs,
  } = useLogs();

  const [fileSearchQuery, setFileSearchQuery] = useState('');
  const [contentSearchQuery, setContentSearchQuery] = useState('');
  const [filterLinesOnly, setFilterLinesOnly] = useState(false);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const logContentRef = useRef(null);
  const matchRefs = useRef([]);

  useEffect(() => {
    fetchLogFiles();
  }, [fetchLogFiles]);

  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [logContent]);

  // Filtrage des fichiers par recherche dans la sidebar
  const filteredLogFiles = useMemo(() => {
    if (!fileSearchQuery.trim()) return logFiles;
    const query = fileSearchQuery.toLowerCase().trim();
    return logFiles.filter((file) =>
      file.name.toLowerCase().includes(query)
    );
  }, [logFiles, fileSearchQuery]);

  // Traitement du contenu avec recherche et surlignage
  const { processedContent, matchCount } = useMemo(() => {
    const lines = logContent ? logContent.split('\n') : [];
    const query = contentSearchQuery.trim();
    const hasSearch = query.length > 0;

    if (!hasSearch) {
      return {
        processedContent: lines.map((line) => ({
          html: escapeHtml(line),
          hasMatch: false,
        })),
        matchCount: 0,
      };
    }

    const regex = new RegExp(`(${escapeRegex(query)})`, 'gi');
    let totalMatches = 0;
    const result = lines.map((line) => {
      if (filterLinesOnly && !regex.test(line)) {
        regex.lastIndex = 0;
        return null;
      }
      regex.lastIndex = 0;
      const matches = line.match(regex);
      const count = matches ? matches.length : 0;
      totalMatches += count;

      const escaped = escapeHtml(line);
      const highlighted = hasSearch
        ? escaped.replace(regex, '<mark class="admin-log-search-match">$1</mark>')
        : escaped;

      return {
        html: highlighted,
        hasMatch: count > 0,
      };
    });

    return {
      processedContent: filterLinesOnly ? result.filter(Boolean) : result,
      matchCount: totalMatches,
    };
  }, [logContent, contentSearchQuery, filterLinesOnly]);

  const matchIndexes = useMemo(() => {
    const indexes = [];
    processedContent.forEach((item, lineIndex) => {
      if (item?.hasMatch) indexes.push(lineIndex);
    });
    return indexes;
  }, [processedContent]);

  const totalMatchLines = matchIndexes.length;

  const scrollToMatch = useCallback(
    (index) => {
      if (matchIndexes.length === 0) return;
      const safeIndex = Math.max(
        0,
        Math.min(index, matchIndexes.length - 1)
      );
      setCurrentMatchIndex(safeIndex);
      const lineIndex = matchIndexes[safeIndex];
      const el = matchRefs.current[lineIndex];
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el?.classList.add('admin-log-line-highlight-scroll');
      setTimeout(
        () => el?.classList.remove('admin-log-line-highlight-scroll'),
        1500
      );
    },
    [matchIndexes]
  );

  const handleCopyLogs = useCallback(() => {
    const textToCopy = filterLinesOnly
      ? processedContent.filter(Boolean).map((p) => {
          const div = document.createElement('div');
          div.innerHTML = p.html;
          return div.textContent;
        }).join('\n')
      : logContent;
    navigator.clipboard.writeText(textToCopy).catch(() => {});
  }, [logContent, processedContent, filterLinesOnly]);

  return (
    <section className="admin-section">
      <h2>📄 Visualisation des logs</h2>

      <div className="admin-logs-container">
        <div className="admin-logs-sidebar">
          <div className="admin-logs-header">
            <h3>Fichiers de logs</h3>
            <button
              className="admin-button admin-button-secondary"
              onClick={fetchLogFiles}
            >
              Actualiser
            </button>
          </div>

          {/* Recherche dans la liste des fichiers */}
          <div className="admin-logs-search-files">
            <FaSearch className="admin-logs-search-icon" />
            <input
              type="text"
              className="admin-logs-search-input"
              placeholder="Filtrer les fichiers..."
              value={fileSearchQuery}
              onChange={(e) => setFileSearchQuery(e.target.value)}
              aria-label="Rechercher dans les noms de fichiers"
            />
          </div>

          {logFiles.length === 0 ? (
            <p className="admin-info">Aucun fichier de log trouvé.</p>
          ) : (
            <div className="admin-logs-list">
              {filteredLogFiles.length === 0 ? (
                <p className="admin-info">Aucun fichier ne correspond.</p>
              ) : (
                filteredLogFiles.map((file) => (
                  <div
                    key={file.name}
                    className={`admin-log-file ${
                      selectedLogFile?.filename === file.name ? 'active' : ''
                    }`}
                    onClick={() => fetchLogContent(file.name)}
                  >
                    <div className="admin-log-file-name">{file.name}</div>
                    <div className="admin-log-file-info">
                      <span>
                        Modifié:{' '}
                        {new Date(file.modified).toLocaleDateString('fr-FR')}
                      </span>
                      <span>{(file.size / 1024).toFixed(1)} KB</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          <div className="admin-logs-cleanup">
            <h4>Nettoyage des logs</h4>
            <p className="admin-info">
              Supprimer les fichiers de logs de plus de {DEFAULT_LOG_CLEANUP_DAYS} jours.
            </p>
            <button
              className="admin-button admin-button-danger"
              onClick={() => handleCleanupLogs(DEFAULT_LOG_CLEANUP_DAYS)}
              disabled={maintenanceLoading}
            >
              {maintenanceLoading
                ? 'Nettoyage...'
                : 'Nettoyer les anciens logs'}
            </button>
          </div>
        </div>

        <div className="admin-logs-content">
          {logLoading ? (
            <div className="admin-loading">
              <div className="admin-spinner"></div>
              <p>Chargement du fichier de log...</p>
            </div>
          ) : logError ? (
            <AdminResult isSuccess={false} title="Erreur" message={logError} />
          ) : selectedLogFile ? (
            <div className="admin-log-viewer">
              <div className="admin-log-header">
                <h3>{selectedLogFile.filename}</h3>
                <div className="admin-log-actions">
                  <button
                    type="button"
                    className="admin-button admin-button-secondary admin-button-sm"
                    onClick={handleCopyLogs}
                    title="Copier le contenu"
                  >
                    <FaCopy /> Copier
                  </button>
                  <div className="admin-log-info">
                    <span>
                      Taille: {(selectedLogFile.size / 1024).toFixed(1)} KB
                    </span>
                    <span>Lignes: {selectedLogFile.lines}</span>
                    <span>
                      Modifié:{' '}
                      {new Date(selectedLogFile.modified).toLocaleString('fr-FR')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Barre de recherche dans le contenu */}
              <div className="admin-log-search-bar">
                <div className="admin-log-search-input-wrap">
                  <FaSearch className="admin-logs-search-icon" />
                  <input
                    ref={logContentRef}
                    type="text"
                    className="admin-logs-search-input"
                    placeholder="Rechercher dans le contenu..."
                    value={contentSearchQuery}
                    onChange={(e) => {
                      setContentSearchQuery(e.target.value);
                      setCurrentMatchIndex(0);
                    }}
                    aria-label="Rechercher dans le contenu des logs"
                  />
                </div>
                <label className="admin-log-search-filter-label">
                  <input
                    type="checkbox"
                    checked={filterLinesOnly}
                    onChange={(e) => setFilterLinesOnly(e.target.checked)}
                  />
                  Filtrer les lignes
                </label>
                {contentSearchQuery.trim() && (
                  <div className="admin-log-search-nav">
                    <span className="admin-log-search-count">
                      {matchCount} occurrence{matchCount !== 1 ? 's' : ''}
                      {filterLinesOnly &&
                        ` (${totalMatchLines} ligne${totalMatchLines !== 1 ? 's' : ''})`}
                    </span>
                    <div className="admin-log-search-nav-btns">
                      <button
                        type="button"
                        className="admin-button admin-button-secondary admin-button-sm"
                        onClick={() => scrollToMatch(currentMatchIndex - 1)}
                        disabled={matchIndexes.length === 0 || currentMatchIndex <= 0}
                        title="Occurrence précédente"
                      >
                        <FaChevronUp />
                      </button>
                      <span className="admin-log-search-nav-index">
                        {matchIndexes.length > 0
                          ? `${currentMatchIndex + 1} / ${totalMatchLines}`
                          : '—'}
                      </span>
                      <button
                        type="button"
                        className="admin-button admin-button-secondary admin-button-sm"
                        onClick={() => scrollToMatch(currentMatchIndex + 1)}
                        disabled={
                          matchIndexes.length === 0 ||
                          currentMatchIndex >= matchIndexes.length - 1
                        }
                        title="Occurrence suivante"
                      >
                        <FaChevronDown />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="admin-log-content">
                <div className="admin-log-content-pre">
                  {processedContent.map((item, index) => {
                    if (!item) return null;
                    return (
                      <div
                        key={index}
                        ref={(el) => {
                          matchRefs.current[index] = el;
                        }}
                        className={`admin-log-line ${item.hasMatch ? 'admin-log-line-match' : ''}`}
                        dangerouslySetInnerHTML={{ __html: item.html || '&nbsp;' }}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="admin-log-placeholder">
              <p>
                Sélectionnez un fichier de log pour afficher son contenu.
              </p>
            </div>
          )}
        </div>
      </div>

      {maintenanceResult && (
        <AdminResult isSuccess={true} title="Résultat du nettoyage">
          <p>
            <strong>Message:</strong> {maintenanceResult.message}
          </p>
          {maintenanceResult.data?.deletedFiles && (
            <div className="admin-cleanup-results">
              <strong>Fichiers supprimés:</strong>
              <ul>
                {maintenanceResult.data.deletedFiles.map((file, index) => (
                  <li key={index}>{file}</li>
                ))}
              </ul>
            </div>
          )}
        </AdminResult>
      )}

      {maintenanceError && (
        <AdminResult isSuccess={false} title="Erreur" message={maintenanceError} />
      )}
    </section>
  );
};

export default LogsTab;
